import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, ExternalLink, Phone, Quote } from 'lucide-react';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import SiteFooter from '../components/SiteFooter';
import Breadcrumbs from '../components/Breadcrumbs';
import DirectAnswer from '../components/DirectAnswer';
import { Button } from '../components/ui/button';
import { PUBLIC_SITE_URL } from '../data/seo';
import { PRACTICE, WHATSAPP_LINK } from '../data/practice';
import { getBreadcrumbLD, getReviewsLD, getFAQPageLD } from '../data/structuredData';

const API = process.env.REACT_APP_BACKEND_URL;
const WRITE_REVIEW_URL = 'https://search.google.com/local/writereview?placeid=ChIJrcl3J7sPdkgRTUPfDpRa1cw';

const Stars = ({ n, size = 'w-4 h-4' }) => (
  <span className="inline-flex" aria-label={`${n} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`${size} ${i <= n ? 'fill-[#F4B400] text-[#F4B400]' : 'text-gray-300'}`} aria-hidden="true" />)}
  </span>
);

const reviewsFAQs = [
  { question: 'Where do these reviews come from?', answer: 'They are genuine Google reviews left by Restore.Dental patients on our Google Business Profile, shown here word for word. Click "Read on Google" on any review to see the original.' },
  { question: 'What is Restore.Dental\'s Google rating?', answer: 'Restore.Dental is rated 4.9 out of 5 on Google from patients treated by Dr Kartik Patel in Acton, West London.' },
  { question: 'Do you treat nervous patients?', answer: 'Yes — many of our reviews come from patients who were anxious or had avoided the dentist for years. Appointments are unhurried, everything is explained first, and sedation is available.' },
];

export default function Reviews() {
  const [data, setData] = useState({ rating: 4.9, review_count: 49, reviews: [], treatments: [], loading: true });
  const [filter, setFilter] = useState('');
  useEffect(() => {
    fetch(`${API}/api/reviews/all`).then((r) => r.json()).then((d) => setData({ ...d, loading: false })).catch(() => setData((s) => ({ ...s, loading: false })));
  }, []);
  const shown = useMemo(() => (filter ? data.reviews.filter((r) => r.treatments?.includes(filter)) : data.reviews), [data.reviews, filter]);
  const crumbs = [{ name: 'Home', to: '/' }, { name: 'Patient Reviews', to: '/reviews' }];
  const rating = Number(data.rating || 4.9);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`Restore.Dental Reviews — ${rating.toFixed(1)}★ from ${data.review_count} Google Reviews | Acton`}
        description={`Read genuine Google reviews of Restore.Dental and Dr Kartik Patel BDS in Acton, West London. Rated ${rating.toFixed(1)}/5 from ${data.review_count} patients for Invisalign, implants, bonding and nervous-patient care.`}
        path="/reviews"
        keywords={['restore dental reviews', 'dr kartik patel reviews', 'dentist acton reviews', 'best dentist acton', 'invisalign reviews london']}
        structuredData={[
          getReviewsLD({ rating, count: data.review_count, reviews: data.reviews }),
          getBreadcrumbLD(crumbs.map((c) => ({ name: c.name, url: `${PUBLIC_SITE_URL}${c.to}` }))),
          getFAQPageLD(reviewsFAQs),
        ]}
      />
      <PageHeader />
      <main className="pt-28 md:pt-32">
        <section className="container mx-auto px-4 max-w-5xl">
          <Breadcrumbs items={crumbs} />
          <p className="text-sm font-semibold uppercase tracking-widest text-[#006A71] mt-6 mb-3">Patient reviews · Google</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-rabbit leading-tight mb-6">What Patients Say About Restore.Dental</h1>

          <div className="flex flex-wrap items-center gap-4 mb-6" data-testid="reviews-summary">
            <span className="text-5xl font-bold text-rabbit">{rating.toFixed(1)}</span>
            <div>
              <Stars n={Math.round(rating)} size="w-6 h-6" />
              <p className="text-sm text-chocolate-malt mt-1">Based on {data.review_count} Google reviews</p>
            </div>
            <a href={data.google_maps_uri || PRACTICE.links.googleMaps} target="_blank" rel="noopener noreferrer" className="ml-auto inline-flex items-center gap-1 text-sm text-[#006A71] underline underline-offset-4" data-testid="reviews-google-link">See all on Google<ExternalLink className="w-4 h-4" aria-hidden="true" /></a>
          </div>

          <DirectAnswer
            answer={`Restore.Dental in Acton, West London is rated ${rating.toFixed(1)} out of 5 on Google from ${data.review_count} patient reviews. Patients most often praise Dr Kartik Patel for gentle, unhurried care that puts nervous patients at ease, clear explanations of every option, and high-quality results in crowns, Invisalign, bonding and implants.`}
            bullets={[`${rating.toFixed(1)}★ Google rating`, `${data.review_count} verified reviews`, 'Nervous patients welcome', 'Reviewed word for word from Google']}
          />

          {data.treatments.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-8" aria-label="Filter reviews by topic">
              <button type="button" onClick={() => setFilter('')} className={`px-4 py-2 rounded-full border text-sm ${!filter ? 'bg-rabbit text-white border-rabbit' : 'border-sky-gray/30 text-rabbit hover:bg-sky-gray/10'}`} data-testid="reviews-filter-all">All reviews</button>
              {data.treatments.map((t) => (
                <button key={t} type="button" onClick={() => setFilter(t)} className={`px-4 py-2 rounded-full border text-sm ${filter === t ? 'bg-rabbit text-white border-rabbit' : 'border-sky-gray/30 text-rabbit hover:bg-sky-gray/10'}`} data-testid={`reviews-filter-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{t}</button>
              ))}
            </div>
          )}
        </section>

        <section className="container mx-auto px-4 max-w-5xl mt-10">
          {data.loading && <div className="grid md:grid-cols-2 gap-6">{[0, 1, 2, 3].map((i) => <div key={i} className="h-48 rounded-2xl bg-sky-gray/10 animate-pulse" />)}</div>}
          {!data.loading && shown.length === 0 && <p className="text-chocolate-malt" data-testid="reviews-empty">No reviews match this filter yet.</p>}
          <div className="grid md:grid-cols-2 gap-6" data-testid="reviews-grid">
            {shown.map((r) => (
              <article key={r.id} className="rounded-2xl border border-sky-gray/20 bg-white p-6 shadow-sm flex flex-col" itemScope itemType="https://schema.org/Review" data-testid="review-card">
                <div className="flex items-center gap-3 mb-3">
                  {r.author_photo ? <img src={r.author_photo} alt="" width="40" height="40" className="w-10 h-10 rounded-full" loading="lazy" referrerPolicy="no-referrer" /> : <span className="w-10 h-10 rounded-full bg-sky-gray/20 text-rabbit font-bold flex items-center justify-center">{r.author?.[0] || 'P'}</span>}
                  <div>
                    <p className="font-semibold text-rabbit" itemProp="author">{r.author}</p>
                    <div className="flex items-center gap-2 text-xs text-chocolate-malt/80">
                      <Stars n={r.rating} />
                      {r.date && <time dateTime={r.date} itemProp="datePublished">{new Date(r.date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</time>}
                    </div>
                  </div>
                  <Quote className="w-6 h-6 text-sky-gray/40 ml-auto" aria-hidden="true" />
                </div>
                <p className="text-chocolate-malt leading-relaxed flex-1 whitespace-pre-line" itemProp="reviewBody">{r.text}</p>
                {r.reply && <p className="mt-4 text-sm text-chocolate-malt/90 border-l-2 border-[#01A9B4] pl-3"><strong>Reply from Restore.Dental:</strong> {r.reply}</p>}
                <div className="flex flex-wrap items-center gap-2 mt-4">
                  {r.treatments?.map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-sky-gray/10 text-[#006A71]">{t}</span>)}
                  {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer nofollow" className="ml-auto text-xs text-chocolate-malt underline underline-offset-4">Read on Google</a>}
                </div>
              </article>
            ))}
          </div>
          <div className="mt-10 rounded-2xl bg-sky-gray/10 p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-rabbit">Been a patient? Leave a review</h2>
              <p className="text-chocolate-malt text-sm mt-1">Your feedback helps other nervous patients find gentle care. It takes 60 seconds on Google.</p>
            </div>
            <Button asChild className="bg-[#4285F4] hover:bg-[#3367D6] text-white"><a href={WRITE_REVIEW_URL} target="_blank" rel="noopener noreferrer" data-testid="write-review-btn">Write a Google review</a></Button>
          </div>
        </section>

        <section className="py-16 mt-6">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-8">About our reviews</h2>
            <div className="space-y-6">
              {reviewsFAQs.map((f) => (
                <div key={f.question} className="border-b border-sky-gray/20 pb-6">
                  <h3 className="text-lg font-bold text-rabbit mb-2">{f.question}</h3>
                  <p className="text-chocolate-malt leading-relaxed">{f.answer}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white"><a href={PRACTICE.phoneTel} data-testid="reviews-call-btn"><Phone className="w-5 h-5 mr-2" />Call {PRACTICE.phoneDisplay}</a></Button>
              <Button asChild size="lg" className="bg-[#25D366] hover:bg-[#20BA5A] text-white"><a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">WhatsApp us</a></Button>
              <Button asChild size="lg" variant="outline"><Link to="/before-after">See before &amp; after photos</Link></Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
